import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const InnovationDetail = () => {
  const { slug } = useParams();

  const innovationData = {
    'r-d-solutions': {
      title: 'R&D Solutions',
      subtitle: 'Custom Research & Development for Your Bakery',
      hero: 'At Enessa Foods, innovation is a collaborative journey. We partner with you to create the future of baking through comprehensive R&D solutions.',
      achievements: [
        { year: '2020', title: 'Pioneering Egg-Free Cake Premixes', desc: 'We pioneered egg-free cake premixes in India—giving bakeries the ability to craft high-quality, vegan-friendly cakes at scale.' },
        { year: '2021', title: 'Custom Formulation Development', desc: 'Developed over 50 custom formulations for leading bakery chains across India, addressing unique production challenges.' },
        { year: '2022', title: 'Process Optimization Success', desc: 'Helped bakeries reduce production costs by up to 20% through advanced process optimization and ingredient functionality testing.' }
      ],
      features: [
        { title: 'Data-Driven Baking Excellence', subtitle: 'DIGITAL PROCESS MONITORING', desc: 'Our digital solutions provide real time process monitoring, giving you the tools to track and optimize production. These innovations will transform the bakery industry by making operations more data-driven, efficient, and precise.' },
        { title: 'Custom Formulation Development', desc: 'We work closely with you to develop custom formulations tailored to your specific needs, optimizing ingredients for cost, quality, and performance.' },
        { title: 'Partnering For Progress', desc: 'We welcome feedback from bakers like you to co-create next-gen products for solving unique production challenges together with real-world solutions.' }
      ]
    },
    'new-technologies': {
      title: 'New Technologies',
      subtitle: 'Cutting-Edge Bakery Technologies',
      hero: 'From ultrasonic cutting to clean-label improvers, Enessa Foods is revolutionizing Indian baking with advanced technologies and next-gen ingredient solutions.',
      achievements: [
        { year: '2020', title: 'Pioneering Egg-Free Cake Premixes', desc: 'We pioneered egg-free cake premixes in India—giving bakeries the ability to craft high-quality, vegan-friendly cakes at scale.' },
        { year: '2021', title: 'Encapsulated Acids for Preservation', desc: 'First in India to introduce encapsulated sorbic & fumaric acids, allowing gradual release for longer freshness—without compromising taste.' },
        { year: '2022', title: 'ENESSA BAKE DIAMOND Improver', desc: 'An industry-favorite improver tailored for chakki atta breads, enhancing softness, volume & consistency in tough Indian climates.' }
      ],
      features: [
        { title: 'Enhanced Natural Preservatives', desc: 'Say goodbye to synthetic additives—our natural preservative systems keep baked goods fresh, clean-label, and shelf-stable.' },
        { title: 'Smarter Baking With Technology', subtitle: 'INDUSTRY 4.0 IN BAKING', desc: 'Enessa Foods is also exploring the Industry 4.0 solutions, integrating IoT and smart sensors into bakery production. This allows us to optimize ovens, mixers, and processes for perfect, consistent results every time.' },
        { title: 'Plant-Based Ingredients', desc: "We're developing sustainable, plant-based ingredients that support India's rising demand for nutritious, eco-conscious bakery products." }
      ]
    },
    'product-development': {
      title: 'Product Development',
      subtitle: 'From Concept to Market-Ready Products',
      hero: 'We help you bring new bakery products to market with comprehensive development support, from initial concept through to commercial production.',
      achievements: [
        { year: '1994', title: 'Establishment', desc: 'Launched as a premier provider of innovative bakery ingredients and solutions tailored for Indian conditions.' },
        { year: '2000', title: 'Egg-Free Cake Premixes', desc: "Successfully launched India's first comprehensive line of egg-free cake premixes, offering a cost-effective solution for vegan bakeries." },
        { year: '2005', title: 'Encapsulated Acid Technology', desc: 'Pioneered encapsulated preservatives, improving the shelf life of bakery products without affecting their flavor or texture.' }
      ],
      features: [
        { title: 'Future-Proof Bakery Solutions', desc: "We're investing in fiber-rich, protein-forward, and plant-powered solutions that meet tomorrow's baking demands—today." },
        { title: 'Concept to Commercial', desc: 'From ideation and validation through recipe development, sensory evaluation, and scale-up support—we guide you every step of the way.' },
        { title: 'Market Launch Assistance', desc: 'Expert technical guidance, consumer-tested formulations, and regulatory compliance support to ensure successful product launches.' }
      ]
    },
    'industry-research': {
      title: 'Industry Research',
      subtitle: 'Market Insights & Trend Analysis',
      hero: 'Stay informed with our comprehensive industry research, market analysis, and trend forecasting to help you make strategic business decisions.',
      achievements: [
        { year: '2010', title: 'Custom Improver Solutions', desc: 'Collaborated with leading bakery chains to create custom improver blends that addressed key industry challenges such as bread staling in hot climates.' },
        { year: '2015', title: 'Innovation Recognition', desc: 'Enessa Foods became a trusted development partner for bakeries seeking to create trend-setting products and modernize their production processes.' },
        { year: '2020', title: 'Customer Adoption', desc: 'Our innovations like ENESSA BAKE DIAMOND have been adopted across the country, helping bakeries create high-quality, consistent baked goods.' }
      ],
      features: [
        { title: 'Data-Driven Insights', subtitle: 'MARKET INTELLIGENCE', desc: 'Our comprehensive market research provides you with actionable insights on consumer preferences, competitive landscape, and emerging trends in the bakery industry.' },
        { title: 'Trend Forecasting', desc: 'Stay ahead of the curve with our trend analysis and forecasting, helping you identify opportunities and make strategic decisions for your business.' },
        { title: 'Technical White Papers', desc: 'Access our library of technical white papers covering ingredient innovations, regulatory updates, and best practices in bakery production.' }
      ]
    }
  };

  const data = innovationData[slug] || innovationData['r-d-solutions'];

  return (
    <div className="min-h-screen bg-white lg:bg-cream">
      <Header />

      {/* Hero Section */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="text-4xl lg:text-6xl font-bold text-[#3b1b0d] mb-4">{data.title}</h1>
          <p className="text-xl lg:text-2xl text-[#b42b2b] mb-8">{data.subtitle}</p>
          <p className="text-lg max-w-3xl mx-auto text-[#7b5b3a]">{data.hero}</p>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-[#3b1b0d] mb-4">Key Achievements</h2>
          <p className="text-[#7b5b3a] mb-12">Innovation milestones that shaped our journey in {data.title.toLowerCase()}.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.achievements.map((item, idx) => (
              <div key={idx} className="bg-cream p-8 rounded-3xl shadow-sm border border-[#f2e1c5] hover:shadow-md transition-all text-left">
                <div className="text-[#b42b2b] font-semibold mb-2">{item.year}</div>
                <h3 className="text-xl font-semibold mb-3 text-[#3b1b0d]">{item.title}</h3>
                <p className="text-[#7b5b3a] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#3b1b0d] mb-12 text-center">Our Approach</h2>
          <div className="space-y-12">
            {data.features.map((feature, idx) => (
              <div key={idx} className="bg-white p-8 lg:p-12 rounded-3xl shadow-sm hover:shadow-md transition-all">
                {feature.subtitle && (
                  <p className="text-[#b42b2b] text-xs font-bold tracking-wider uppercase mb-2">{feature.subtitle}</p>
                )}
                <h3 className="text-2xl lg:text-3xl font-bold text-[#3b1b0d] mb-4">{feature.title}</h3>
                <p className="text-[#7b5b3a] text-base lg:text-lg leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#3b1b0d] mb-6">Ready to Innovate?</h2>
          <p className="text-lg text-[#7b5b3a] mb-8">
            Let's discuss how our {data.title.toLowerCase()} can help transform your bakery business.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="px-6 py-3 bg-[#b42b2b] text-white rounded-full font-medium hover:bg-[#8f2222] transition-colors">
              Contact Us
            </Link>
            <Link to="/innovation" className="px-6 py-3 bg-transparent border-2 border-[#b42b2b] text-[#b42b2b] rounded-full font-medium hover:bg-[#b42b2b] hover:text-white transition-colors">
              Back to Innovation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InnovationDetail;
